import React, { useState, useRef } from 'react';
import axios from 'axios';
import { checkForAuthorization } from 'utilities/index';
import Button from '@mui/material/Button';
import { Box, Typography, Avatar, Modal, Grid, InputLabel, Divider } from '@mui/material';
import CustomLabel from '../CustomLabel';
import { useField, useFormikContext } from 'formik';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { Primary, Secondary } from 'ui-components/Buttons';
import theme from '../../themes/theme';
import { ReactComponent as UploadDocumentsIcon } from "../../assets/rounds_icons/upload_documents_icon.svg";
import { Close } from '@mui/icons-material';
import CustomValidationText from '../CustomValidationText';

const blobToFile = (blob, fileName) => {
    return new File([blob], fileName, { type: blob.type });
};

// The crop box cannot show WebP everywhere, so a WebP picture is converted to PNG first.
const webpToPng = (file) => new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        canvas.toBlob((blob) => {
            URL.revokeObjectURL(url);
            if (!blob) return reject(new Error('convert'));
            resolve(new File([blob], file.name.replace(/\.webp$/i, '.png'), { type: 'image/png' }));
        }, 'image/png');
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('convert')); };
    img.src = url;
});

// Browsers do not always report a MIME type (Finder and some apps leave it empty), so the extension counts too.
const SUPPORTED_IMAGE_EXTENSIONS = /\.(png|jpe?g)$/i;
const isSupportedImage = (file) => {
    if (file?.type === 'image/png' || file?.type === 'image/jpeg' || file?.type === 'image/jpg') return true;
    return !file?.type && SUPPORTED_IMAGE_EXTENSIONS.test(file?.name || '');
};
const isHeicOrOtherImage = (file) => /image\//.test(file?.type || '') || /\.heic$/i.test(file?.name || '');
const UNSUPPORTED_IMAGE_MESSAGE = 'Use a JPG or PNG image. iPhone photos are HEIC: export or share them as JPG first.';

const CustomUploadButton = ({ label, handleUpdateFile, initials = '', name, value, Pwidth = 150, Pheight = 150, Pmultiplier = 0.95, placeholder }) => {
    const [selectedFile, setSelectedFile] = useState(value || null);
    const [imagePreview, setImagePreview] = useState(value || null);
    const [previousImage, setPreviousImage] = useState(value || null);
    const [isRemoveButtonPressed, setIsRemoveButtonPressed] = useState(true)
    const [isUploadButtonPressed, setIsUploadButtonPressed] = useState(true)
    const [field, meta] = useField(name);
    const { setFieldValue, submitCount } = useFormikContext();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const cropperRef = useRef(null);

    const [uploadError, setUploadError] = useState('');

    const handleFileChange = (file) => {
        if (isSupportedImage(file)) {
            setUploadError('');
            setPreviousImage(imagePreview);
            setSelectedFile(file);
            const imageUrl = URL.createObjectURL(file);
            setImagePreview(imageUrl);
            setIsModalOpen(true);
        } else if (file && (isHeicOrOtherImage(file) || file.type === '')) {
            // Said out loud, not dropped silently: a HEIC file opens a crop box that cannot show it.
            setUploadError(UNSUPPORTED_IMAGE_MESSAGE);
        }
    };

    const handleFileInputChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            handleFileChange(file);
            event.target.value = null; // Reset the input value to allow the same file to be selected again
        }
    };

    const handleCropButton = async (event) => {
        event.preventDefault();
        try {
            const cropper = cropperRef.current.cropper;
            const croppedCanvas = cropper.getCroppedCanvas();
            if (croppedCanvas) {
                croppedCanvas.toBlob((blob) => {
                    const fileName = selectedFile ? selectedFile.name : 'cropped-image.png';
                    const croppedFile = blobToFile(blob, fileName);
                    const croppedImageUrl = URL.createObjectURL(croppedFile);
                    // setFieldValue(name, croppedFile).then(() => {
                    //     event.preventDefault();
                    // });
                    handleUpdateFile(croppedFile);
                    setIsModalOpen(false);
                    setImagePreview(croppedImageUrl);
                });
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handleCancelButton = () => {
        setIsModalOpen(false);
        setSelectedFile(null);
        setImagePreview(previousImage);
    };

    const handlePreviewClick = () => {
        if (imagePreview) {
            setTimeout(() => {
                setIsModalOpen(true);
            }, 1000);
        }
    };

    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: '#FFFFFF',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        borderRadius: '24px',
        p: 5,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '90vw',
        maxWidth: '520px',
        maxHeight: '80dvh',
        overflow: 'auto',
    };

    const cropperContainerStyle = {
        width: '100%',
        height: '350px',
        borderRadius: '12px',
        overflow: 'hidden',
    };

    const buttonContainerStyle = {
        display: 'flex',
        gap: '16px',
        marginTop: '24px',
        justifyContent: 'center',
        width: '100%'
    };

    React.useEffect(() => {
        if (value && (value instanceof Blob || value instanceof File)) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target.result);
            };
            reader.readAsDataURL(value);
        } else if (typeof value === 'string') {
            setImagePreview(value);
        } else {
            setImagePreview(null);
        }
    }, [value]);


    // Dragging a picture from another web page (Google Images, a website) gives a link, not a file.
    // Take the link, fetch the picture, and treat it as a dropped file.
    const imageUrlFromDrop = (dataTransfer) => {
        const uriList = dataTransfer.getData('text/uri-list');
        const fromList = uriList?.split('\n').map((line) => line.trim()).find((line) => line && !line.startsWith('#'));
        if (fromList) return fromList;
        const html = dataTransfer.getData('text/html');
        const match = html && html.match(/<img[^>]+src=["']([^"']+)["']/i);
        return match ? match[1] : '';
    };

    // The server downloads the picture (a browser cannot, because of CORS) and sends back the bytes as a data URL.
    const fileFromImageUrl = async (url) => {
        const response = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/startups/fetch-remote-image`,
            { url },
            { headers: { Authorization: `Bearer ${checkForAuthorization()}` } }
        );
        const { content_type: type, data_url: dataUrl } = response.data.data;
        const rawName = decodeURIComponent(new URL(url).pathname.split('/').pop() || '') || 'dropped-image';
        const base = rawName.replace(/\.[a-z0-9]+$/i, '') || 'dropped-image';
        const bytes = Uint8Array.from(atob(dataUrl.split(',')[1]), (c) => c.charCodeAt(0));
        const file = new File([bytes], `${base}.${type === 'image/webp' ? 'png' : type.split('/')[1] === 'jpeg' ? 'jpg' : type.split('/')[1]}`, { type });
        return type === 'image/webp' ? webpToPng(file) : file;
    };

    const handleDrop = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        const dataTransfer = event.dataTransfer;
        const file = dataTransfer.files[0];
        if (file) {
            handleFileChange(file);
            return;
        }
        const url = imageUrlFromDrop(dataTransfer);
        if (!url) {
            setUploadError('Drop a PNG, JPG or JPEG file from your computer.');
            return;
        }
        try {
            handleFileChange(await fileFromImageUrl(url));
        } catch (e) {
            // Many sites block downloading their pictures this way, so tell the user the way that always works.
            setUploadError('This site does not let us take its picture. Right-click the picture, choose Save image as, then drop that saved file here.');
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    return (
        <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
        >
            <CustomLabel>{label}</CustomLabel>
            <input
                accept=".png, .jpg, .jpeg"
                style={{ display: 'none' }}
                id={name ? name : "upload-button-file"}
                type="file"
                onChange={handleFileInputChange}
            />

            <Box
                sx={{
                    borderRadius: '12px',
                    display: 'grid',
                    placeItems: 'center',
                    padding: '1rem',
                    color: '#667085',
                    backgroundColor: '#FFF',
                    backgroundClip: 'padding-box',
                    textTransform: 'none',
                    borderWidth: '1px',
                    borderStyle: 'dashed',
                    borderColor: '#CBCBD6',
                    ":hover": {
                        backgroundColor: "rgba(255,255,255,1)",
                        borderColor: '#CBCBD6',
                    }
                }}
            >
                <> <label htmlFor={name ? name : "upload-button-file"} style={{ display: 'grid', placeItems: 'center', width: '100%' }}>{
                    imagePreview ? (
                        <>
                            <Box
                                component="img"
                                src={typeof imagePreview !== "object" && !imagePreview?.includes("data:image") && !imagePreview?.includes("blob:") ? imagePreview + `?cacheBuster=${new Date().getTime()}` : imagePreview}
                                alt="Uploaded Preview"
                                loading='eager'
                                rel='preload'
                                sx={{
                                    borderRadius: '16px',
                                    objectFit: 'cover',
                                    border: `1px solid rgb(102, 112, 133,0.5)`,
                                    background: theme.palette.customColors.g50,
                                    width: Pwidth,
                                    height: Pheight,
                                    marginInline: 'auto'
                                }}
                                onClick={handlePreviewClick} // Trigger modal on preview click
                            />
                            <Grid item container xs={12} display={"flex"} alignItems="center" flexWrap={"nowrap"} justifyContent={"center"} gap={1} mt={"1.5rem"} >

                                <Grid xs={5.5} item sx={{ display: 'flex', justifyContent: 'center', maxWidth: 'max-content' }}>
                                    <InputLabel onMouseDown={() => setIsUploadButtonPressed(false)} onMouseUp={() => setIsUploadButtonPressed(true)} htmlFor={name ? name : "upload-button-file"} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', opacity: isUploadButtonPressed ? 1 : 0.2, cursor: 'pointer', gap: 5, width: '100%' }}>
                                        <UploadDocumentsIcon
                                            style={{
                                                width: 16,
                                                height: 16,
                                                stroke: 'rgb(105, 65, 198)'
                                            }}
                                        />
                                        <span className='highlight' style={{ color: "#6941C6", textAlign: 'center' }}>Update</span>
                                    </InputLabel>
                                </Grid>
                                <Divider orientation="vertical" flexItem />
                                {<Grid xs={5.5} item sx={{ maxWidth: 'max-content' }}>
                                    <InputLabel onClick={() => handleUpdateFile(null)} onMouseDown={() => setIsRemoveButtonPressed(false)} onMouseUp={() => setIsRemoveButtonPressed(true)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: isRemoveButtonPressed ? 1 : 0.2, cursor: 'pointer', gap: 0.5, maxWidth: 'max-content', width: '100%' }}>
                                        <Close sx={{ width: 16, height: 16, color: 'rgb(239, 83, 80)', margin: 0 }} />
                                        <span className='' style={{ color: "rgb(239, 83, 80)", textAlign: 'center' }}>Remove</span>
                                    </InputLabel>
                                </Grid>}
                            </Grid>
                        </>
                    ) : (
                        <>
                            {/* {console.log(placeHolderImage,"placeHolderImage")} */}
                            {placeholder ? <Box
                                sx={{
                                    borderRadius: '16px',
                                    width: Pwidth,
                                    height: Pheight,
                                    display: 'grid',
                                    placeContent: 'center'
                                }}
                            >
                                <Box
                                    component="img"
                                    src={placeholder}
                                    alt="Uploaded Preview"
                                    loading='eager'
                                    rel='preload'
                                    sx={{
                                        objectFit: 'contain',
                                        width: Pwidth * Pmultiplier,
                                        height: Pheight * Pmultiplier,
                                    }}
                                    onClick={handlePreviewClick} // Trigger modal on preview click
                                />
                            </Box> :
                                <Avatar sx={{ width: Pwidth, height: Pheight, borderRadius: '10px' }} />}
                            <div style={{ marginTop: "0.5rem", marginBottom: "0.25rem" }}>
                                <span className='highlight' style={{ color: "#6941C6", fontSize: '0.875rem' }}>Click to upload </span>
                                <span style={{ fontSize: '0.875rem' }}>or drag and drop</span>
                            </div>
                            {/* <small>SVG, PNG, JPG or GIF (max 512 × 512px)</small> */}
                            <small style={{ fontSize: '12px' }}>PNG, JPG or JPEG</small>
                        </>
                    )}
                </label>
                </>
            </Box>
            {
                (uploadError || (meta && meta.error)) && (
                    <CustomValidationText value={uploadError || meta.error} />
                )
            }

            <Modal
                open={isModalOpen}
                onClose={() => { }}
                aria-labelledby="modal-crop-title"
                aria-describedby="modal-crop-description"
            >
                <Box sx={modalStyle}>
                    <Typography
                        id="modal-crop-title"
                        variant="h6"
                        component="h2"
                        sx={{ textAlign: 'center', mb: 3, fontWeight: 700, fontSize: '1.5rem', color: '#101828' }}
                    >
                        Crop Logo
                    </Typography>
                    <Box sx={cropperContainerStyle}>
                        <Cropper
                            src={imagePreview}
                            style={{ width: '100%', height: '100%' }}
                            aspectRatio={1}
                            guides={true}
                            width={500}
                            height={200}
                            ref={cropperRef}
                            viewMode={2}
                        />
                    </Box>
                    <Box sx={buttonContainerStyle}>
                        <Secondary type='button' onClick={(e) => { handleCancelButton(e); }} sx={{ py: 1.8 }} fullWidth>
                            Back
                        </Secondary>
                        <Primary type='button' onClick={(e) => { handleCropButton(e); }} sx={{ py: 1.8 }} fullWidth>
                            Select
                        </Primary>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default CustomUploadButton;
